import { 
  Cpu, 
  HardDrive, 
  MemoryStick, 
  Wifi, 
  Shield,
  AlertTriangle,
  CheckCircle,
  Play
} from 'lucide-react'
import { Link } from 'react-router-dom'

// Mock data - will be replaced with real API calls
const systemStatus = {
  overall: 'good',
  cpu: { status: 'good', usage: 23, temp: 45 },
  memory: { status: 'good', usage: 58, total: 32, used: 18.5 },
  disk: { status: 'warning', usage: 78, total: 1000, used: 780 },
  network: { status: 'good', ping: 12, download: 245, upload: 45 },
  security: { status: 'good', lastScan: '2 hours ago', threats: 0 },
}

const statusColors = {
  good: 'text-health-good',
  warning: 'text-health-warning',
  critical: 'text-health-critical',
}

const StatusIcon = ({ status }: { status: string }) => {
  if (status === 'good') return <CheckCircle className="w-5 h-5 text-health-good" />
  if (status === 'warning') return <AlertTriangle className="w-5 h-5 text-health-warning" />
  return <AlertTriangle className="w-5 h-5 text-health-critical" />
}

export function HomePage() {
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">System Overview</h1>
        <p className="text-slate-400">
          Your PC is running {systemStatus.overall === 'good' ? 'smoothly' : 'with some issues'}
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4 mb-8">
        <Link to="/diagnostics" className="btn-primary flex items-center gap-2">
          <Play className="w-4 h-4" />
          Run Full Scan
        </Link>
        <Link to="/chat" className="btn-secondary flex items-center gap-2">
          Ask AI Assistant
        </Link>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* CPU */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Cpu className="w-5 h-5 text-blue-400" />
              </div>
              <span className="font-medium text-white">CPU</span>
            </div>
            <StatusIcon status={systemStatus.cpu.status} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Usage</span>
              <span className={statusColors[systemStatus.cpu.status as keyof typeof statusColors]}>
                {systemStatus.cpu.usage}%
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${systemStatus.cpu.usage}%` }}
              />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Temperature</span>
              <span className="text-slate-300">{systemStatus.cpu.temp}°C</span>
            </div>
          </div>
        </div>

        {/* Memory */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <MemoryStick className="w-5 h-5 text-purple-400" />
              </div>
              <span className="font-medium text-white">Memory</span>
            </div>
            <StatusIcon status={systemStatus.memory.status} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Usage</span>
              <span className={statusColors[systemStatus.memory.status as keyof typeof statusColors]}>
                {systemStatus.memory.used} / {systemStatus.memory.total} GB
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ width: `${systemStatus.memory.usage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Disk */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-amber-400" />
              </div>
              <span className="font-medium text-white">Storage</span>
            </div>
            <StatusIcon status={systemStatus.disk.status} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Usage</span>
              <span className={statusColors[systemStatus.disk.status as keyof typeof statusColors]}>
                {systemStatus.disk.used} / {systemStatus.disk.total} GB
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 rounded-full transition-all"
                style={{ width: `${systemStatus.disk.usage}%` }}
              />
            </div>
            {systemStatus.disk.status === 'warning' && (
              <p className="text-xs text-health-warning mt-2">
                ⚠️ Consider freeing up some disk space
              </p>
            )}
          </div>
        </div>

        {/* Network */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Wifi className="w-5 h-5 text-green-400" />
              </div>
              <span className="font-medium text-white">Network</span>
            </div>
            <StatusIcon status={systemStatus.network.status} />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Ping</span>
              <span className="text-slate-300">{systemStatus.network.ping} ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Download</span>
              <span className="text-slate-300">{systemStatus.network.download} Mbps</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Upload</span>
              <span className="text-slate-300">{systemStatus.network.upload} Mbps</span>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="font-medium text-white">Security</span>
            </div>
            <StatusIcon status={systemStatus.security.status} />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Last Scan</span>
              <span className="text-slate-300">{systemStatus.security.lastScan}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Threats Found</span>
              <span className="text-health-good">{systemStatus.security.threats}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
